import axios from "axios";
import React, { useState, useEffect, useMemo } from "react";
import { FaRegEdit, FaRegTrashAlt } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import apiKey from "../../../config/apiKey";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

/** ---------- Config (easy future switch) ---------- */
const RESOURCE_LABEL = "Profession";
const RESOURCE_LABEL_PLURAL = "Professions";
// If your API later becomes /professions, just change here:
const ENDPOINT_BASE = "classes"; // e.g. "professions" in future

/** ---------- Small UI helpers ---------- */
const SkeletonRow = () => (
  <tr className="animate-pulse">
    {[...Array(3)].map((_, i) => (
      <td key={i} className="py-4 px-1">
        <div className="h-4 w-32 bg-gray-200 rounded" />
      </td>
    ))}
  </tr>
);

const AcademicClass = () => {
  const { t } = useTranslation();
  const admin = JSON.parse(localStorage.getItem("edo-admin"));

  // Create
  const [newProfessionName, setNewProfessionName] = useState("");
  const [formError, setFormError] = useState("");

  // List + fetch state
  const [items, setItems] = useState([]); // [{ id, profession_name }]
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  // Search + pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Edit modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState({ id: null, profession_name: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch list
  const loadItems = () => {
    setLoading(true);
    setFetchError("");
    axios
      .get(`${apiKey}/api/edolutions/admin/${ENDPOINT_BASE}`, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      })
      .then((res) => {
        // map API class_name -> profession_name for UI consistency
        const list = (res.data || []).map((r) => ({
          id: r.id,
          profession_name: r.class_name ?? r.profession_name ?? "",
        }));
        setItems(list);
      })
      .catch((err) => {
        console.error(err);
        setFetchError("Couldn’t load data. Please try again.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.token]);

  // Create (Add Profession)
  const addProfession = () => {
    if (!newProfessionName.trim()) {
      setFormError(t("adminDash.academicClass.nameRequired"));
      return;
    }
    setFormError("");

    axios
      .post(
        `${apiKey}/api/edolutions/admin/${ENDPOINT_BASE}`,
        // backend still expects class_name — keep payload key as class_name for compatibility
        { class_name: newProfessionName },
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      )
      .then((response) => {
        toast.success(response.data?.message || t("adminDash.academicClass.addedSuccess"), {
          position: "bottom-right",
          autoClose: 4000,
          theme: "colored",
          style: { backgroundColor: "#2A3A54", color: "#fff" },
        });
        setNewProfessionName("");
        loadItems();
      })
      .catch((error) => {
        console.error("Error adding profession:", error);
        toast.error(t("adminDash.academicClass.addError"), {
          position: "bottom-right",
          autoClose: 5000,
          theme: "colored",
        });
      });
  };

  // Edit
  const openEdit = (id, profession_name) => {
    setCurrentItem({ id, profession_name });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentItem({ id: null, profession_name: "" });
  };

  const updateProfession = () => {
    axios
      .put(
        `${apiKey}/api/edolutions/admin/${ENDPOINT_BASE}/${currentItem.id}`,
        { class_name: currentItem.profession_name }, // keep backend payload key
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      )
      .then((response) => {
        toast.success(response.data?.message || t("adminDash.academicClass.updatedSuccess"), {
          position: "bottom-right",
          autoClose: 4000,
          theme: "colored",
          style: { backgroundColor: "#2A3A54", color: "#fff" },
        });
        closeModal();
        loadItems();
      })
      .catch((error) => {
        console.error("Error updating profession:", error);
        toast.error(t("adminDash.academicClass.updateError"), {
          position: "bottom-right",
          autoClose: 5000,
          theme: "colored",
        });
      });
  };

  // Delete
  const deleteProfession = (id) => {
    axios
      .delete(`${apiKey}/api/edolutions/admin/${ENDPOINT_BASE}/${id}`, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      })
      .then((response) => {
        setItems((prev) => prev.filter((x) => x.id !== id));
        toast.success(response.data?.message || t("adminDash.academicClass.removedSuccess"), {
          position: "bottom-right",
          autoClose: 4000,
          theme: "colored",
          style: { backgroundColor: "#2A3A54", color: "#fff" },
        });
      })
      .catch((error) => {
        console.error("Error deleting profession:", error);
        toast.error(t("adminDash.academicClass.deleteError"), {
          position: "bottom-right",
          autoClose: 5000,
          theme: "colored",
        });
      });
  };

  // Derived: filter + paginate (memoized)
  const filtered = useMemo(() => {
    const term = debouncedSearch.toLowerCase();
    if (!term) return items;
    return items.filter((it) => (it.profession_name || "").toLowerCase().includes(term));
  }, [items, debouncedSearch]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = Math.min((currentPage - 1) * itemsPerPage, totalItems);
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginated = useMemo(() => filtered.slice(startIndex, endIndex), [filtered, startIndex, endIndex]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, debouncedSearch]);

  // Windowed page numbers (max 5)
  const pageRange = useMemo(() => {
    const maxBtns = 5;
    if (totalPages <= maxBtns) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - (maxBtns - 1)));
    return Array.from({ length: maxBtns }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  return (
    <div className="relative my-8 flex flex-col w-full shadow-sm bg-white rounded-2xl border border-gray-200">
      {/* Header: Add Profession */}
      <div className="px-6 pt-6">
        <p className="text-xl font-bold">{t("adminDash.academicClass.addTitle")}</p>
        <div className="mt-4 mb-2 flex max-sm:flex-col max-sm:items-stretch gap-2">
          <input
            type="text"
            className="border p-2 shadow-sm w-full sm:w-80 rounded-md outline-none"
            placeholder={t("adminDash.academicClass.namePlaceholder")}
            value={newProfessionName}
            onChange={(e) => {
              setNewProfessionName(e.target.value);
              setFormError("");
            }}
          />
          <button
            onClick={addProfession}
            className="bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:from-[#2A3A54] hover:to-[#EE5F2B] text-sm font-semibold rounded-md text-white px-5 py-2"
          >
            {t("adminDash.common.submit")}
          </button>
        </div>
        {(formError || fetchError) && (
          <p className="text-red-600 text-sm mb-2">{formError || fetchError}</p>
        )}
      </div>

      {/* Toolbar */}
      <div className="px-6 py-4 flex flex-wrap items-center justify-between border-t border-gray-100">
        <p className="text-lg font-semibold">{t("adminDash.academicClass.listTitle")}</p>
        <div className="relative border rounded-md flex items-center px-4 py-2 shadow-sm">
          <FiSearch className="h-5 w-5 absolute left-2 text-gray-500" />
          <input
            type="text"
            className="outline-none pl-7 text-sm"
            placeholder={t("adminDash.academicClass.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="mx-6 mb-6 overflow-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-50 text-[#6b7280] sticky top-0 z-10">
            <tr>
              <th className="py-3 px-2 font-medium w-1/12">{t("adminDash.common.sr")}</th>
              <th className="py-3 px-2 font-medium">{t("adminDash.academicClass.colName")}</th>
              <th className="py-3 px-2 font-medium text-center w-2/12">{t("adminDash.common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y text-center">
            {loading && (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}

            {!loading && paginated.length === 0 && (
              <tr>
                <td colSpan={3} className="py-8 px-2 text-gray-500">
                  {t("adminDash.academicClass.noData")}
                </td>
              </tr>
            )}

            {!loading &&
              paginated.map((item, idx) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="py-4 px-2">{startIndex + idx + 1}</td>
                  <td className="py-4 px-2 text-left">{item.profession_name}</td>
                  <td className="py-4 px-2">
                    <div className="flex justify-center gap-6">
                      <button
                        onClick={() => openEdit(item.id, item.profession_name)}
                        className="text-[#2A3A54] hover:opacity-80"
                        title={t("adminDash.common.edit")}
                      >
                        <FaRegEdit className="text-lg" />
                      </button>
                      <button
                        onClick={() => deleteProfession(item.id)}
                        className="text-rose-600 hover:text-rose-700"
                        title={t("adminDash.common.delete")}
                      >
                        <FaRegTrashAlt className="text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap gap-5 justify-between items-center w-full text-sm px-6 pb-6">
        <div className="flex items-center gap-2 text-gray-600">
          <select
            className="text-sm border rounded-lg px-2 py-1 bg-white"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(parseInt(e.target.value, 10))}
          >
            {[10, 20, 30, 40, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span>{t("adminDash.common.showing")}</span>
          <span className="font-medium text-gray-800">
            {totalItems === 0 ? 0 : startIndex + 1}-{endIndex}
          </span>
          <span>{t("adminDash.common.of")} {totalItems} {t("adminDash.common.entries")}</span>
        </div>

        <div className="flex items-center gap-2 text-gray-600">
          <button
            className="text-base bg-gray-100 rounded-lg px-2 py-1 disabled:opacity-40"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label={t("adminDash.common.previousPage")}
          >
            <MdKeyboardArrowLeft />
          </button>

          {pageRange.map((p) => (
            <button
              key={p}
              className={`text-base rounded-lg px-3 py-1 ${
                p === currentPage ? "text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]"  : "text-black bg-gray-100"
              }`}
              onClick={() => setCurrentPage(p)}
            >
              {p}
            </button>
          ))}

          <span>{t("adminDash.common.of")} {totalPages} {t("adminDash.common.pages")}</span>

          <button
            className="text-base bg-gray-100 rounded-lg px-2 py-1 disabled:opacity-40"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            aria-label={t("adminDash.common.nextPage")}
          >
            <MdKeyboardArrowRight />
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="px-5 py-4 border-b">
              <h2 className="text-lg font-semibold">{t("adminDash.academicClass.updateTitle")}</h2>
            </div>
            <div className="px-5 py-5">
              <input
                type="text"
                className="border p-2 w-full rounded-md shadow-sm"
                value={currentItem.profession_name}
                onChange={(e) =>
                  setCurrentItem({ ...currentItem, profession_name: e.target.value })
                }
                placeholder={t("adminDash.academicClass.colName")}
              />
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button
                onClick={closeModal}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm"
              >
                {t("adminDash.common.cancel")}
              </button>
              <button
                onClick={updateProfession}
                className="bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:opacity-90 text-white px-4 py-2 rounded-lg text-sm"
              >
                {t("adminDash.common.update")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicClass;
